import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { LIST_BOOKMARKS, UNBOOKMARK_MESSAGE } from '../graphql/operations';
import './BookmarksView.css';

/**
 * KAP-311.3 — saved-for-later view. Lists every bookmark for the calling
 * user (denormalized snapshot rows on the per-user partition). Each row:
 *   - Conversation title + sender display name
 *   - Content excerpt (already truncated to 280 chars server-side)
 *   - Relative time bookmarked
 *   - Open → navigates to /conversations/<id>?msg=<msgId> (the existing
 *     `?msg=` deep-link handler in UnifiedChatPanel scrolls + pulses)
 *   - Remove → fires `unbookmarkMessage`, with a cache.modify-style update
 *     so the row disappears optimistically.
 *
 * Pagination: cursor-based via `nextCursor` from BookmarkConnection. The
 * "Load more" button advances when the connection signals `hasMore`.
 */
const PAGE_SIZE = 50;

function formatRelative(timestamp) {
  if (!timestamp) return '';
  const then = new Date(timestamp).getTime();
  if (!Number.isFinite(then)) return '';
  const now = Date.now();
  const diffMs = now - then;
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(then).toLocaleDateString();
}

function BookmarksView() {
  const navigate = useNavigate();
  const { data, loading, fetchMore } = useQuery(LIST_BOOKMARKS, {
    variables: { limit: PAGE_SIZE },
    fetchPolicy: 'cache-and-network'
  });

  const [unbookmarkMessageMutation] = useMutation(UNBOOKMARK_MESSAGE);

  const items = data?.listBookmarks?.items || [];
  const hasMore = !!data?.listBookmarks?.hasMore;
  const nextCursor = data?.listBookmarks?.nextCursor;

  const handleOpen = (bookmark) => {
    if (!bookmark?.conversationId || !bookmark?.messageId) return;
    navigate(
      `/conversations/${encodeURIComponent(bookmark.conversationId)}?msg=${encodeURIComponent(bookmark.messageId)}`
    );
  };

  const handleRemove = async (bookmark) => {
    try {
      await unbookmarkMessageMutation({
        variables: {
          messageId: bookmark.messageId,
          conversationId: bookmark.conversationId
        },
        update: (cache) => {
          // Filter the bookmark out of LIST_BOOKMARKS optimistically. Same
          // pattern as the pinned-banner cache.modify in UnifiedChatPanel.
          try {
            const existing = cache.readQuery({
              query: LIST_BOOKMARKS,
              variables: { limit: PAGE_SIZE }
            });
            const current = existing?.listBookmarks?.items || [];
            cache.writeQuery({
              query: LIST_BOOKMARKS,
              variables: { limit: PAGE_SIZE },
              data: {
                listBookmarks: {
                  ...existing.listBookmarks,
                  items: current.filter((row) => row.id !== bookmark.id)
                }
              }
            });
          } catch (_err) {
            // Cache miss → next query refetches from network.
          }
        }
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to remove bookmark', err);
    }
  };

  const handleLoadMore = () => {
    if (!hasMore || !nextCursor) return;
    fetchMore({
      variables: { cursor: nextCursor, limit: PAGE_SIZE },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult?.listBookmarks) return prev;
        const prevItems = prev?.listBookmarks?.items || [];
        const merged = [...prevItems, ...fetchMoreResult.listBookmarks.items];
        return {
          listBookmarks: {
            ...fetchMoreResult.listBookmarks,
            items: merged
          }
        };
      }
    });
  };

  return (
    <div className="bookmarks-view">
      <header className="bookmarks-view-header">
        <h1>Bookmarks</h1>
        <p className="bookmarks-view-subtitle">
          Messages you&apos;ve saved for later. Only you can see this list.
        </p>
      </header>

      {loading && items.length === 0 ? (
        <p className="bookmarks-view-empty">Loading bookmarks…</p>
      ) : items.length === 0 ? (
        <p className="bookmarks-view-empty">
          No bookmarks yet. Open any message&apos;s kebab menu and pick
          &ldquo;Bookmark message&rdquo; to save it here.
        </p>
      ) : (
        <ul className="bookmarks-view-list">
          {items.map((bookmark) => (
            <li key={bookmark.id} className="bookmarks-view-card">
              <div className="bookmarks-view-card-header">
                <span className="bookmarks-view-conversation">
                  {bookmark.conversationTitle || bookmark.conversationId}
                </span>
                <span className="bookmarks-view-time">
                  {formatRelative(bookmark.bookmarkedAt)}
                </span>
              </div>
              <div className="bookmarks-view-card-sender">
                {bookmark.senderDisplayName || bookmark.senderId || 'Unknown'}
              </div>
              <p className="bookmarks-view-card-excerpt">{bookmark.contentExcerpt}</p>
              <div className="bookmarks-view-card-actions">
                <button
                  type="button"
                  className="bookmarks-view-btn"
                  onClick={() => handleOpen(bookmark)}
                >
                  Open
                </button>
                <button
                  type="button"
                  className="bookmarks-view-btn bookmarks-view-btn-destructive"
                  onClick={() => handleRemove(bookmark)}
                >
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {hasMore && (
        <div className="bookmarks-view-load-more">
          <button type="button" className="bookmarks-view-btn" onClick={handleLoadMore} disabled={loading}>
            {loading ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}

export default BookmarksView;
